import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../lib/supabase";
import { formatTimeAgo } from "../../utils/formatters";

// =============================================================================
// RECORDING PLAYER — standardized custom transport
// =============================================================================
// PURPOSE: one consistent audio player for a stored dial recording, the same
// on every /dialview surface. Native <audio controls> was replaced because it
// looks different per browser and cannot host a skip-15 or a hover speed menu.
//
// BEHAVIOR:
// - Returns null when neither recordingUrl nor src is set (no recording exists).
// - autoLoad (detail panel only): signs + buffers on mount, NO autoplay, so the
//   first Play is instant (no click→sign→wait hitch). Use ONLY where a single
//   player is on screen — never a list (one storage call per selected call).
// - Without autoLoad (feed rows): stays lazy — signs on the first Play press so
//   never-played recordings cost zero storage calls.
// - Transport: back 15s · play/pause · forward 15s · click-to-seek bar · times ·
//   /triangle speed menu (1×–2×). A failed/expired signed URL re-signs once.
//
// - Plain `src` (any public URL, e.g. a handbook voice note): no signing, no
//   bucket. recordingUrl wins when both are set. `label` replaces "Recording".
//
// STYLE: light-mode glass-neutral wrapper, width 100% of parent.
// =============================================================================

const BUCKET = "dial-recordings";
const URL_TTL_SEC = 3600; // 1 hour
const SKIP_SEC = 15;
const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;
const FLYOUT_CLOSE_DELAY = 220; // ms — grace on leave so the travel gap doesn't dismiss it

interface RecordingPlayerProps {
  /** Storage path like "{org_id}/{ghl_message_id}.ogg" (dial-recordings bucket, signed on demand) */
  recordingUrl?: string | null;
  /** Plain playable URL (e.g. "/handbook-audio/rkpi.m4a"). Skips signing. Ignored if recordingUrl is set. */
  src?: string | null;
  /** First word of the label line. Default "Recording". */
  label?: string;
  /** ISO date for label */
  occurredAt?: string;
  /** Duration in seconds for label + scrubber total (e.g. 83 → "1:23") */
  durationSec?: number;
  /** Fires on each playback tick with currentTime in seconds — drives active-word highlight. */
  onTimeUpdate?: (currentSec: number) => void;
  /** Sign + buffer on mount so the first Play is instant. ONE player on screen only. */
  autoLoad?: boolean;
}

// Imperative handle for click-to-seek: a transcript word click calls seekTo with
// its start second. The player owns the <audio> element (and its lazy signed-URL
// activation), so seeking has to route through here, not through a raw audio ref.
export interface RecordingPlayerHandle {
  /** Jump playback to `seconds` and play. Activates the player first if idle. */
  seekTo: (seconds: number) => void;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function buildLabel(occurredAt?: string, durationSec?: number, first = "Recording"): string {
  const parts: string[] = [first];
  if (occurredAt) {
    try {
      parts.push(formatTimeAgo(occurredAt));
    } catch {
      parts.push(new Date(occurredAt).toLocaleString());
    }
  }
  if (durationSec != null && durationSec > 0) {
    parts.push(formatDuration(durationSec));
  }
  return parts.join(" • ");
}

// -----------------------------------------------------------------------------
// SpeedFlyout — /triangle hover-and-click speed picker.
// Single-trigger flyout, so the triangle rule = defer the CLOSE with a grace
// delay on leave (a moving cursor between trigger and menu must not dismiss it).
// Portalled to body (a glass container is a paint/stacking trap). Opacity only,
// never a transform transition on glass. Keyboard/touch open immediately (no aim).
// -----------------------------------------------------------------------------
const SpeedFlyout: React.FC<{ rate: number; onPick: (r: number) => void }> = ({ rate, onPick }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = () => {
    clearClose();
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 6 });
    setOpen(true);
  };
  const scheduleClose = () => {
    clearClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), FLYOUT_CLOSE_DELAY);
  };
  useEffect(() => clearClose, []);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        onClick={openNow}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Playback speed ${rate}×`}
        data-testid="recording-speed"
        className="anim-tap shrink-0 h-8 min-w-[3rem] rounded-full bg-black/[0.06] hover:bg-black/[0.1] px-2.5 token-11 tabular-nums text-glass-secondary transition-colors"
      >
        {rate}×
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            role="menu"
            onMouseEnter={openNow}
            onMouseLeave={scheduleClose}
            style={{
              position: "fixed",
              left: pos.left,
              bottom: pos.bottom,
              transform: "translateX(-50%)",
            }}
            className="z-[10050] detail-overlay rounded-lg shadow-xl p-1 flex flex-col min-w-[4rem]"
          >
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                role="menuitemradio"
                aria-checked={s === rate}
                onClick={() => {
                  onPick(s);
                  setOpen(false);
                }}
                className={`anim-tap text-center px-3 py-1.5 rounded-md token-11 tabular-nums transition-colors ${
                  s === rate
                    ? "bg-ds-accent/[0.10] text-ds-accent"
                    : "text-glass-secondary hover:bg-black/[0.05]"
                }`}
              >
                {s}×
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

// Skip-15 icon: a circular arrow. `dir` -1 = counter-clockwise (back), +1 = cw.
const SkipIcon: React.FC<{ dir: -1 | 1 }> = ({ dir }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={dir === 1 ? { transform: "scaleX(-1)" } : undefined}
  >
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-4.36L1 10" />
  </svg>
);

const RecordingPlayer = forwardRef<RecordingPlayerHandle, RecordingPlayerProps>(
  function RecordingPlayer(
    {
      recordingUrl,
      src,
      label: labelFirst,
      occurredAt,
      durationSec,
      onTimeUpdate,
      autoLoad = false,
    },
    ref
  ) {
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [signing, setSigning] = useState(false);
    // Asked to play, but the audio is still downloading (slow network): show the spinner.
    const [buffering, setBuffering] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [total, setTotal] = useState<number>(durationSec ?? 0);
    const [rate, setRate] = useState<number>(1);
    const rateRef = useRef(1);
    const retriedRef = useRef(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // A seek requested before the player was activated: hold + apply on metadata.
    const pendingSeekRef = useRef<number | null>(null);
    // Play once the src is ready (a click/seek path), vs just buffer (autoLoad).
    const playOnReadyRef = useRef(false);

    // What to play: a bucket path (signed) or a plain URL (as is).
    const source = recordingUrl || src || null;

    const activate = useCallback(
      async (path: string, playOnReady: boolean) => {
        if (!recordingUrl && src) {
          playOnReadyRef.current = playOnReady;
          setError(null);
          setBuffering(playOnReady);
          setSignedUrl(src); // plain URL: nothing to sign
          return;
        }
        if (!supabase) {
          setError("Supabase not configured");
          return;
        }
        playOnReadyRef.current = playOnReady;
        setSigning(true);
        setError(null);
        const { data, error: sbError } = await supabase.storage
          .from(BUCKET)
          .createSignedUrl(path, URL_TTL_SEC);
        setSigning(false);
        if (sbError || !data?.signedUrl) {
          setError(sbError?.message ?? "Failed to load recording");
          return;
        }
        setBuffering(playOnReady);
        setSignedUrl(data.signedUrl);
      },
      [recordingUrl, src]
    );

    // autoLoad: sign + buffer on mount (no autoplay) so the first Play is instant.
    useEffect(() => {
      if (autoLoad && source && !signedUrl) void activate(source, false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad, source]);

    const applySeek = (seconds: number) => {
      const el = audioRef.current;
      if (!el) return;
      el.currentTime = seconds;
      void el.play().catch(() => setBuffering(false)); // blocked/aborted play: no spinner left behind
    };

    const togglePlay = () => {
      const el = audioRef.current;
      if (!el || !signedUrl) {
        if (source) void activate(source, true); // lazy path: sign then play
        return;
      }
      if (el.paused)
        void el.play().catch(() => setBuffering(false)); // blocked/aborted play: no spinner left behind
      else el.pause();
    };

    const skip = (delta: number) => {
      const el = audioRef.current;
      if (!el) return;
      const dur = total || el.duration || 0;
      el.currentTime = Math.max(0, Math.min(dur, el.currentTime + delta));
    };

    const pickSpeed = (r: number) => {
      rateRef.current = r;
      setRate(r);
      if (audioRef.current) audioRef.current.playbackRate = r;
    };

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (seconds: number) => {
          if (!source) return;
          if (audioRef.current && signedUrl) {
            applySeek(seconds);
          } else {
            pendingSeekRef.current = seconds;
            if (!signedUrl) void activate(source, false);
          }
        },
      }),
      [source, signedUrl, activate]
    );

    if (!source) return null;

    // Src-level playback failure: usually an expired signed URL. Re-sign once.
    const handleAudioError = () => {
      setBuffering(false);
      if (retriedRef.current || (!recordingUrl && src)) {
        setSignedUrl(null);
        setError("Playback failed");
        return;
      }
      retriedRef.current = true;
      setSignedUrl(null);
      void activate(source, playOnReadyRef.current);
    };

    const seekFromClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const el = audioRef.current;
      const dur = total || el?.duration || 0;
      if (!el || !signedUrl || !dur) {
        if (!signedUrl && source) void activate(source, false);
        return;
      }
      const bar = e.currentTarget.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - bar.left) / bar.width));
      el.currentTime = frac * dur;
      setCurrent(el.currentTime);
    };

    const label = buildLabel(occurredAt, durationSec, labelFirst);
    const pct = total > 0 ? Math.min(100, (current / total) * 100) : 0;
    const skipBtn =
      "anim-tap shrink-0 h-8 px-2 flex items-center gap-1 rounded-full bg-black/[0.06] hover:bg-black/[0.1] text-glass-secondary transition-colors";

    return (
      <div className="w-full rounded-lg border border-black/[0.08] bg-black/[0.03] px-3 py-2.5">
        <p className="token-11 text-glass-muted mb-2 select-none">{label}</p>

        {error ? (
          <div>
            <p className="token-11 text-glass-secondary">{labelFirst ?? "Recording"} unavailable</p>
            <p className="token-11 text-glass-muted mt-0.5">{error}</p>
          </div>
        ) : (
          <>
            {/* Headless audio — the custom transport below drives it */}
            <audio
              ref={audioRef}
              preload="auto"
              src={signedUrl ?? undefined}
              onError={handleAudioError}
              onLoadedMetadata={() => {
                const el = audioRef.current;
                if (!el) return;
                el.playbackRate = rateRef.current;
                if (Number.isFinite(el.duration) && el.duration > 0) setTotal(el.duration);
                if (playOnReadyRef.current) {
                  void el.play().catch(() => setBuffering(false)); // blocked/aborted play: no spinner left behind
                  playOnReadyRef.current = false;
                }
                if (pendingSeekRef.current != null) {
                  applySeek(pendingSeekRef.current);
                  pendingSeekRef.current = null;
                }
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => {
                setIsPlaying(false);
                setBuffering(false);
              }}
              onEnded={() => {
                setIsPlaying(false);
                setBuffering(false);
              }}
              onWaiting={(e) => !e.currentTarget.paused && setBuffering(true)}
              onPlaying={() => setBuffering(false)}
              onTimeUpdate={(e) => {
                const t = (e.currentTarget as HTMLAudioElement).currentTime;
                setCurrent(t);
                onTimeUpdate?.(t);
              }}
              className="hidden"
            />

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5">
              {/* Back 15 */}
              <button
                type="button"
                onClick={() => skip(-SKIP_SEC)}
                aria-label="Back 15 seconds"
                data-testid="recording-back15"
                className={`${skipBtn} order-1 sm:order-none`}
              >
                <SkipIcon dir={-1} />
                <span className="token-11 font-semibold tabular-nums leading-none">15</span>
              </button>

              {/* Play / pause */}
              <button
                type="button"
                onClick={togglePlay}
                disabled={signing}
                aria-label={isPlaying ? "Pause" : "Play"}
                data-testid="recording-play"
                className="anim-tap order-2 sm:order-none shrink-0 h-9 w-9 flex items-center justify-center rounded-full bg-ds-accent/[0.12] hover:bg-ds-accent/[0.2] text-ds-accent transition-colors disabled:opacity-60"
              >
                {signing || buffering ? (
                  <span className="h-4 w-4 rounded-full border-2 border-current border-b-transparent animate-spin" />
                ) : isPlaying ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <rect x="3.5" y="2.5" width="3" height="11" rx="1" />
                    <rect x="9.5" y="2.5" width="3" height="11" rx="1" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M4 2.5v11l9-5.5z" />
                  </svg>
                )}
              </button>

              {/* Forward 15 */}
              <button
                type="button"
                onClick={() => skip(SKIP_SEC)}
                aria-label="Forward 15 seconds"
                data-testid="recording-fwd15"
                className={`${skipBtn} order-3 sm:order-none`}
              >
                <span className="token-11 font-semibold tabular-nums leading-none">15</span>
                <SkipIcon dir={1} />
              </button>

              {/* Current time */}
              {/* Phone row break: buttons + speed on row 1, times + scrubber on row 2. */}
              <div aria-hidden="true" className="order-5 basis-full h-0 sm:hidden" />

              <span className="order-6 sm:order-none token-13m text-glass-secondary tabular-nums shrink-0 sm:ml-1">
                {formatDuration(current)}
              </span>

              {/* Scrubber — click to seek */}
              <div
                onClick={seekFromClick}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={Math.round(total)}
                aria-valuenow={Math.round(current)}
                className="order-7 sm:order-none flex-1 min-w-0 h-6 sm:h-2 flex items-center cursor-pointer group"
              >
                <div className="w-full h-[3px] rounded-full bg-black/[0.12] overflow-hidden group-hover:h-[5px] transition-all">
                  <div className="h-full rounded-full bg-ds-accent" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Total time */}
              <span className="order-8 sm:order-none token-13m text-glass-muted tabular-nums shrink-0">
                {total > 0 ? formatDuration(total) : "--:--"}
              </span>

              {/* Speed picker (/triangle hover menu) */}
              <div className="order-4 ml-auto sm:order-none sm:ml-0 shrink-0">
                <SpeedFlyout rate={rate} onPick={pickSpeed} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

export default RecordingPlayer;
